import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Building2,
  ArrowRight,
  Trash2,
  Pencil,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import { OrganizationType } from "common/constants";
import { Modal } from "components/common/Modal";
import { Tooltip } from "components/common/Tooltip";
import { TruncatedTextTooltip } from "components/common/TruncatedTextTooltip";
import { ProjectEditModal } from "./ProjectEditModal";
import { Action, projectSubject, useAppAbility } from "common/access";

const statusConfig = {
  created: { bg: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-400" },
  in_process: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-400",
  },
  in_validation: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    dot: "bg-blue-400",
  },
  done: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-400",
  },
  error: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-400" },
};

const ProjectCard = ({ project, onDelete, onEdit, onArchive, onUnarchive }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const ability = useAppAbility();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const projectId = project.id || project._id;
  const isArchived = Boolean(project.archivedAt);

  const adminDetails = user?.isGlobalAdmin
    ? t("project_card.admin_details", {
        organization:
          project.organizationName ?? t("project_card.no_organization"),
        id: projectId,
      })
    : null;

  const cardSubject = useMemo(
    () =>
      projectSubject({
        id: Number(projectId),
        organizationId: project.organizationId ?? user?.organizationId,
      }),
    [projectId, project.organizationId, user?.organizationId],
  );

  const canEditProject = ability.can(Action.Update, cardSubject);
  const canDeleteProject = ability.can(Action.Delete, cardSubject);
  const canManageTeam = ability.can(Action.ManageTeam, cardSubject);

  const projectLink =
    user.organizationType === OrganizationType.QUANTITY_SURVEYOR
      ? `/project/${projectId}/smart-surveyor/upload`
      : `/project/${projectId}`;

  const rawStatus = project.status || "created";
  const status = rawStatus.toLowerCase().replace(/ /g, "_");
  const style = statusConfig[status] || statusConfig.created;
  const statusLabel = t(`project_card.status.${status}`, {
    defaultValue: project.status,
  });

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(projectId);
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEditModal(true);
  };

  const handleArchiveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowArchiveConfirm(true);
  };

  const handleConfirmArchive = async () => {
    setArchiving(true);
    try {
      await onArchive(projectId);
    } finally {
      setArchiving(false);
      setShowArchiveConfirm(false);
    }
  };

  const handleCancelArchive = () => {
    setShowArchiveConfirm(false);
  };

  const handleOpenProject = () => {
    trackEvent(ANALYTICS_EVENTS.PROJECT_OPENED, {
      projectId,
      projectTitle: project.title,
    });
  };

  const handleUnarchiveClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setArchiving(true);
    try {
      await onUnarchive(projectId);
    } finally {
      setArchiving(false);
    }
  };

  if (!projectId) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 opacity-60">
        <p className="text-sm text-red-500">
          {t("project_card.invalid_project_data")}
        </p>
      </div>
    );
  }

  return (
    <>
      <Link
        to={projectLink}
        onClick={handleOpenProject}
        className="block h-full group"
      >
        <div className="h-full rounded-xl border border-gray-200 bg-white p-5 flex flex-col transition-all duration-200 hover:border-blue-200 hover:shadow-md hover:shadow-blue-50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Building2 size={20} className="text-blue-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {project.title}
                </h3>
                {adminDetails && (
                  <TruncatedTextTooltip
                    text={adminDetails}
                    className="mt-0.5 block text-xs text-gray-500"
                  />
                )}
                <span
                  className={`mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                  {statusLabel}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {onEdit && canEditProject && (
                <Tooltip label={t("project_edit.edit_project")}>
                  <button
                    onClick={handleEditClick}
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 opacity-0 group-hover:opacity-100 hover:text-blue-500 hover:bg-blue-50 transition-all"
                    aria-label={t("project_edit.edit_project")}
                  >
                    <Pencil size={16} />
                  </button>
                </Tooltip>
              )}
              {canEditProject && isArchived && onUnarchive && (
                <Tooltip label={t("project_card.unarchive_project")}>
                  <button
                    onClick={handleUnarchiveClick}
                    disabled={archiving}
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 opacity-0 group-hover:opacity-100 hover:text-emerald-500 hover:bg-emerald-50 transition-all disabled:opacity-50"
                    aria-label={t("project_card.unarchive_project")}
                  >
                    <ArchiveRestore size={16} />
                  </button>
                </Tooltip>
              )}
              {canEditProject && !isArchived && onArchive && (
                <Tooltip label={t("project_card.archive_project")}>
                  <button
                    onClick={handleArchiveClick}
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 opacity-0 group-hover:opacity-100 hover:text-amber-500 hover:bg-amber-50 transition-all"
                    aria-label={t("project_card.archive_project")}
                  >
                    <Archive size={16} />
                  </button>
                </Tooltip>
              )}
              {onDelete && canDeleteProject && (
                <Tooltip label={t("dashboard.delete_confirm_title")}>
                  <button
                    onClick={handleDeleteClick}
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 transition-all"
                    aria-label={t("dashboard.delete_confirm_title")}
                  >
                    <Trash2 size={16} />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          {project.description && (
            <p className="mt-3 text-sm text-gray-500 line-clamp-2 flex-1">
              {project.description}
            </p>
          )}

          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
            <div className="flex items-center font-medium text-blue-600 group-hover:text-blue-700 transition-colors">
              <span>{t("project_card.view_project_details")}</span>
              <ArrowRight
                size={14}
                className="ml-1 transition-transform group-hover:translate-x-0.5"
              />
            </div>
            {project.createdAt && (
              <span className="text-xs text-gray-400">
                {t("project_card.created_date")}{" "}
                {new Date(project.createdAt).toLocaleDateString(
                  i18n.language === "de" ? "de-DE" : "en-US",
                )}
              </span>
            )}
          </div>
        </div>
      </Link>
      <Modal
        size="sm"
        isOpen={showConfirm}
        onClose={handleCancelDelete}
        disableBackdropClose={deleting}
        disableEscapeClose={deleting}
      >
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto">
          <Trash2 size={22} className="text-red-500" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-gray-900 text-center">
          {t("dashboard.delete_confirm_title")}
        </h3>
        <p className="mt-2 text-sm text-gray-500 text-center">
          {t("dashboard.delete_confirm_message", {
            title: project.title,
          })}
        </p>
        <div className="mt-6 flex gap-3">
          <button
            onClick={handleCancelDelete}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {t("dashboard.delete_cancel")}
          </button>
          <button
            onClick={handleConfirmDelete}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {deleting && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            )}
            <span>{t("dashboard.delete_confirm")}</span>
          </button>
        </div>
      </Modal>
      <Modal
        size="sm"
        isOpen={showArchiveConfirm}
        onClose={handleCancelArchive}
        disableBackdropClose={archiving}
        disableEscapeClose={archiving}
      >
        <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
          <Archive size={22} className="text-amber-500" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-gray-900 text-center">
          {t("dashboard.archive_confirm_title")}
        </h3>
        <p className="mt-2 text-sm text-gray-500 text-center">
          {t("dashboard.archive_confirm_message", {
            title: project.title,
          })}
        </p>
        <div className="mt-6 flex gap-3">
          <button
            onClick={handleCancelArchive}
            disabled={archiving}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {t("dashboard.archive_cancel")}
          </button>
          <button
            onClick={handleConfirmArchive}
            disabled={archiving}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {archiving && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            )}
            <span>{t("dashboard.archive_confirm")}</span>
          </button>
        </div>
      </Modal>
      {showEditModal && (
        <ProjectEditModal
          projectId={projectId}
          title={project.title}
          description={project.description}
          canManageTeam={canManageTeam}
          onSave={(input) => onEdit(projectId, input)}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </>
  );
};

export default ProjectCard;
